import React, { useState, useMemo, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useReadingContextSafe } from '../../contexts/ReadingContext';
import { useMessages } from '../../hooks/useMessages';
import { useShopping } from '../../hooks/useShopping';
import { format, differenceInMinutes, isAfter, addHours } from 'date-fns';
import { 
  Calendar, ShoppingCart, MessageSquare, Users, 
  ChevronRight, AlertCircle, Clock, Star, Plus, Search,
  DollarSign, Bookmark, CheckSquare, Target, ArrowRight, Eye
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import UnifiedBig3Planner from '../Planning/UnifiedBig3Planner';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { parseShoppingItem } from '../../utils/smartItemParser';
import { getRecentActiveShoppingItems } from '../../utils/shoppingHelpers';
import ReadingChallengeWidget from './ReadingChallengeWidget';
import CollegeJourneyWidget from '../College/CollegeJourneyWidget';
import WhatsNewSection from './WhatsNewSection';
import NotificationSetup from '../Shared/NotificationSetup';
import './SmartDashboard.css';

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Next event banner - minimal
const NextEventBanner = ({ event }) => {
  if (!event) return null;
  
  const eventDate = new Date(event.date + 'T' + (event.time || '00:00:00'));
  const minutesUntil = differenceInMinutes(eventDate, new Date());
  const isUrgent = minutesUntil < 60 && minutesUntil > 0;
  
  return (
    <Link to="/calendar" className="block">
      <div className={`p-3 rounded-xl border ${
        isUrgent ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' : 
        'bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-stone-700" />
            <div>
              <p className="font-medium text-sm text-slate-900">{event.title}</p>
              <p className="text-xs text-stone-600">
                {isUrgent ? `In ${minutesUntil} minutes` : format(eventDate, 'h:mm a')}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-400" />
        </div>
      </div>
    </Link>
  );
};

// Upcoming Event Item - warm, touch-friendly mobile design
const UpcomingEventItem = ({ event, familyMembers }) => {
  const eventDate = new Date(event.date + 'T' + (event.time || '00:00:00'));
  const minutesUntil = differenceInMinutes(eventDate, new Date());
  const isToday = minutesUntil < 24 * 60;
  const isUrgent = minutesUntil < 60;
  const needsRSVP = event.rsvp_enabled && !event.rsvpStatus;
  
  // Find who created this event
  const creator = familyMembers?.find(member => member.id === event.created_by);
  
  return (
    <div className={`flex items-center justify-between py-3 px-3 rounded-xl transition-all duration-150 ${
      isUrgent ? 'bg-coral-50 border border-coral-200' : 
      isToday ? 'bg-gold-50 border border-gold-200' : 
      'bg-stone-50 border border-stone-100 hover:bg-stone-100'
    }`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 shadow-sm ${
          isUrgent ? 'bg-coral-500' : 
          isToday ? 'bg-gold-500' : 'bg-teal-500'
        }`} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-slate-900 truncate leading-snug">{event.title}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs font-medium text-stone-600">
              {isToday ? format(eventDate, 'h:mm a') : format(eventDate, 'MMM d, h:mm a')}
            </p>
            {event.location && (
              <>
                <span className="text-xs text-stone-400">•</span>
                <p className="text-xs text-stone-500 truncate">{event.location}</p>
              </>
            )}
            {creator && (
              <>
                <span className="text-xs text-stone-400">•</span>
                <div className="flex items-center gap-1">
                  <AvatarOrInitial
                    avatar={creator.avatar}
                    name={creator.name}
                    size="xs"
                  />
                  <span className="text-xs text-stone-500">{creator.name}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {needsRSVP && (
        <span className="text-xs font-semibold text-coral-700 bg-coral-100 px-3 py-1 rounded-full border border-coral-200 flex-shrink-0 ml-2">
          RSVP
        </span>
      )}
    </div>
  );
};

// Clean Priority Item with cute circular badges (like Sarah's reference)
const CleanPriorityItem = ({ icon: Icon, title, count, area = 'default', onClick }) => {
  // Area-specific badge colors
  const badgeColors = {
    messages: 'bg-azure-500',
    shopping: 'bg-coral-500', 
    calendar: 'bg-teal-500',
    family: 'bg-amber-500',
    tasks: 'bg-emerald-500',
    allowance: 'bg-emerald-500',
    planning: 'bg-violet-500',
    default: 'bg-primary-500'
  };
  
  const badgeColor = badgeColors[area] || badgeColors.default;
  
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-between py-4 px-0 w-full text-left hover:bg-stone-50 active:bg-stone-100 transition-colors rounded-lg -mx-1 px-1"
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-stone-600" />
        </div>
        <span className="text-base font-medium text-slate-900">{title}</span>
      </div>
      
      {count > 0 && (
        <div className={`w-7 h-7 rounded-full ${badgeColor} flex items-center justify-center flex-shrink-0`}>
          <span className="text-white text-sm font-semibold">{count}</span>
        </div>
      )}
    </button>
  );
};

// Main Mobile Dashboard
export default function MobileDashboard({ isMobile, breakpoint } = {}) {
  const { 
    family, 
    currentUser, 
    familyCalendar, 
    familyActivities,
    familyTimeInvites,
    plans,
    allowanceJobs,
    setCurrentUser,
    user
  } = useFamilyContext();
  
  // Reading context for kids only
  const isKid = !currentUser?.isParent;
  const readingContext = useReadingContextSafe();
  const { 
    currentBook, 
    readingStats, 
    activeChallenge, 
    isLoading: readingLoading 
  } = readingContext || {};
  
  const { messages } = useMessages({ recipientId: currentUser?.id });
  const { items: shoppingItems, addItem: addShoppingItem } = useShopping();
  const navigate = useNavigate();
  
  // Shopping quick add state
  const [shoppingQuickAdd, setShoppingQuickAdd] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const shoppingInputRef = useRef(null);
  
  // Handle shopping quick add
  const handleShoppingQuickAdd = async (e) => {
    e.preventDefault();
    if (!shoppingQuickAdd.trim() || isAddingItem) return;
    
    setIsAddingItem(true);
    try {
      const parsedItem = parseShoppingItem(shoppingQuickAdd);
      await addShoppingItem(parsedItem);
      setShoppingQuickAdd('');
      // Refocus input after adding
      setTimeout(() => shoppingInputRef.current?.focus(), 100);
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setIsAddingItem(false);
    }
  };
  
  // Enhanced upcoming events - family-wide and actionable only
  const upcomingEvents = useMemo(() => {
    if (!familyCalendar) return [];
    
    const now = new Date();
    const next30Days = addHours(now, 30 * 24);
    
    // Combine calendar events and confirmed family activities
    const allEvents = [
      ...familyCalendar,
      // Include confirmed family activities that have dates
      ...(familyActivities || [])
        .filter(activity => {
          const hasDate = activity.start_date || activity.date;
          const isConfirmed = activity.confirmed_for_calendar || 
                            ['scheduled', 'Scheduled', 'onCalendar', 'has_dates'].includes(activity.status);
          return hasDate && isConfirmed;
        })
        .map(activity => ({
          ...activity,
          type: 'family-activity',
          title: activity.title || activity.name,
          date: activity.start_date || activity.date,
          time: activity.time || '00:00:00'
        }))
    ];
    
    return allEvents
      .filter(event => {
        const eventDate = new Date(event.date + 'T' + (event.time || '00:00:00'));
        
        // Strict future-only filtering
        if (!isAfter(eventDate, now) || eventDate > next30Days) {
          return false;
        }
        
        // Filter out personal events specific to other family members
        if (event.personal_event && event.created_by !== currentUser?.id) {
          return false;
        }
        
        return true;
      })
      .sort((a, b) => new Date(a.date + 'T' + (a.time || '00:00:00')) - new Date(b.date + 'T' + (b.time || '00:00:00')))
      .slice(0, 3);
  }, [familyCalendar, familyActivities, currentUser]);
  
  // Next immediate event (within 6 hours)
  const nextEvent = upcomingEvents.find(event => {
    const eventDate = new Date(event.date + 'T' + (event.time || '00:00:00'));
    return differenceInMinutes(eventDate, new Date()) < 360;
  });

  // Kids progress for family overview - ADDED FOR MOBILE
  const kidsProgress = useMemo(() => {
    if (!family?.kids || !plans) return [];
    
    const today = format(new Date(), 'yyyy-MM-dd');
    
    return family.kids.map(kid => {
      const todayPlan = plans[`${kid.id}_${today}`] || [];
      const completed = todayPlan.filter(t => t.status === 'completed').length;
      const total = todayPlan.length;
      
      return {
        ...kid,
        completed,
        total,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        hasUrgent: todayPlan.some(t => t.priority === 'urgent' && t.status !== 'completed')
      };
    });
  }, [family, plans]);

  // Helper function to switch to kid view - ADDED FOR MOBILE
  const switchToKidView = (kid) => {
    console.log('Switch to kid view:', kid.name);
    
    // Clear cached data when switching users to avoid stale data
    if (user?.id) {
      localStorage.removeItem(`family-data-${user.id}`);
      localStorage.removeItem(`family-data-timestamp-${user.id}`);
    }
    
    setCurrentUser({
      ...kid,
      isParent: false,
      originalParent: true
    });
    
    // Navigate to kid dashboard
    navigate('/dashboard');
  };
  
  // Count urgent items and calculate detailed context including ALL sidebar notification items
  const actionableItems = useMemo(() => {
    // Family time invites needing response (highest priority)
    const pendingInvites = familyTimeInvites?.filter(invite => 
      invite.status === 'pending' && 
      (invite.invitee === currentUser?.id || invite.created_by === currentUser?.id)
    ) || [];
    
    const urgentMessages = messages.filter(msg => 
      msg.priority === 'urgent' && 
      msg.recipients?.some(r => r.recipient_id === currentUser?.id && r.status === 'unread')
    );
    
    const allUnreadMessages = messages.filter(msg => 
      msg.recipients?.some(r => r.recipient_id === currentUser?.id && r.status === 'unread')
    );
    
    const urgentShopping = shoppingItems.filter(item => 
      item.status !== 'purchased' && item.priority === 'urgent'
    );
    
    const pendingShopping = shoppingItems.filter(item => 
      item.status !== 'purchased'
    );
    
    const unsortedShopping = shoppingItems.filter(item => 
      item.status !== 'purchased' && !item.category
    );
    
    // Separate new ideas from pending votes for better prioritization
    const activeActivities = familyActivities?.filter(activity => 
      !activity.confirmed_for_calendar && 
      !activity.scheduledDate && 
      activity.status !== 'archived'
    ) || [];
    
    const newIdeas = activeActivities.filter(activity => {
      const ratings = activity.ratings || {};
      return Object.keys(ratings).length === 0;
    });
    
    const pendingVotes = activeActivities.filter(activity => {
      const ratings = activity.ratings || {};
      const hasOtherVotes = Object.keys(ratings).length > 0;
      const currentUserHasVoted = ratings[currentUser?.id] !== undefined;
      return hasOtherVotes && !currentUserHasVoted;
    });
    
    // Activities needing RSVP for confirmed events (future events only)
    const pendingRSVPs = familyActivities?.filter(activity => {
      if (!activity.confirmed_for_calendar) return false;
      const rsvps = activity.rsvps || {};
      if (rsvps[currentUser?.id] !== undefined) return false;
      
      // Check if event is in the future
      const eventDate = activity.start_date || activity.date;
      if (eventDate) {
        const eventDateTime = new Date(eventDate + 'T' + (activity.time || '23:59:59'));
        if (!isAfter(eventDateTime, new Date())) return false;
      }
      
      return true;
    }) || [];
    
    const upcomingRSVPs = upcomingEvents.filter(event => 
      event.rsvpRequired && !event.rsvpStatus
    );
    
    const completableTodos = plans?.[`${currentUser?.id}_${format(new Date(), 'yyyy-MM-dd')}`]?.filter(
      todo => todo.status === 'pending'
    ) || [];
    
    const pendingAllowanceJobs = allowanceJobs?.filter(job => 
      job.status === 'pending_approval'
    ) || [];
    
    // Planning reminders - incomplete Big 3
    const todayPlan = plans?.[`${currentUser?.id}_${format(new Date(), 'yyyy-MM-dd')}`] || [];
    const incompleteBig3 = todayPlan.filter(task => task.status !== 'completed').length;
    
    return {
      pendingInvites: pendingInvites.length,
      urgentMessages: urgentMessages.length,
      allMessages: allUnreadMessages.length,
      urgentShopping: urgentShopping.length,
      pendingShopping: pendingShopping.length,
      unsortedShopping: unsortedShopping.length,
      newIdeas: newIdeas.length,
      pendingVotes: pendingVotes.length,
      pendingRSVPs: pendingRSVPs.length,
      upcomingRSVPs: upcomingRSVPs.length,
      completableTodos: completableTodos.length,
      pendingAllowance: pendingAllowanceJobs.length,
      incompleteBig3: incompleteBig3,
      // Context details
      nextInvite: pendingInvites[0]?.activity_idea || pendingInvites[0]?.title,
      nextNewIdea: newIdeas[0]?.title,
      nextVoteActivity: pendingVotes[0]?.title,
      nextRSVPEvent: upcomingRSVPs[0]?.title
    };
  }, [messages, shoppingItems, familyActivities, familyTimeInvites, currentUser, upcomingEvents, plans, allowanceJobs]);
  
  const hasUrgentItems = actionableItems.urgentMessages > 0 || actionableItems.urgentShopping > 0;
  
  // Helper function to return to parent view - ADDED FOR MOBILE
  const handleReturnToParent = () => {
    if (currentUser?.originalParent) {
      // If currently viewing as kid, return to parent view
      const loggedInParent = family?.parents?.find(p => p.authUserId === user?.id);
      
      if (loggedInParent) {
        setCurrentUser({
          ...loggedInParent,
          isParent: true,
          originalParent: false
        });
      } else {
        // Fallback for backward compatibility
        setCurrentUser({
          id: 'parent',
          name: family?.parentName,
          isParent: true,
          originalParent: false
        });
      }
      navigate('/dashboard'); // Stay on mobile dashboard but as parent
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-20">
      <NotificationSetup familyMemberId={currentUser?.id} />
      {/* Enhanced Header with Return to Parent Option */}
      <header className="px-4 pt-6 pb-4">
        {/* Kid View Indicator */}
        {currentUser?.originalParent && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <Eye className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800 font-medium">Viewing {currentUser?.name}'s dashboard</span>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-xl font-light text-slate-900">
              {getGreeting()}, <span className="font-medium">{currentUser?.name}</span>
            </h1>
            <p className="text-sm text-stone-600 mt-0.5">
              {format(new Date(), 'EEEE, MMM d')}
            </p>
          </div>
          
          {/* Return to Parent Button - Only show when viewing as kid */}
          {currentUser?.originalParent && (
            <button
              onClick={handleReturnToParent}
              className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 active:bg-slate-800 transition-colors font-medium text-sm shadow-sm"
              title="Return to parent view"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </button>
          )}
        </div>
      </header>
      
      {/* Alert Bar - Only if urgent */}
      {hasUrgentItems && (
        <div className="mx-4 mb-3 p-3 bg-coral-50 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-coral-600" />
          <span className="text-sm text-coral-900 font-medium">
            {actionableItems.urgentMessages + actionableItems.urgentShopping} urgent items
          </span>
        </div>
      )}
      
      {/* Next Event - Only if within 6 hours */}
      {nextEvent && (
        <div className="px-4 mb-4">
          <NextEventBanner event={nextEvent} />
        </div>
      )}
      
      {/* Big 3 - Always visible anchor */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="dashboard-big3-container">
            <UnifiedBig3Planner 
              user={currentUser} 
              date={new Date()} 
              compact={false}
              isMobile={isMobile}
              breakpoint={breakpoint}
            />
          </div>
        </div>
      </div>

      {/* Family Progress - Show kids' Big 3 progress */}
      {currentUser?.isParent && kidsProgress.length > 0 && (
        <div className="px-4 mb-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 border-l-4 border-l-teal-500">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-600" />
                <h3 className="font-semibold text-slate-900 text-sm">Family Progress</h3>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {kidsProgress.map(kid => (
                <button 
                  key={kid.id} 
                  onClick={() => switchToKidView(kid)}
                  className="relative bg-stone-50 rounded-xl p-3 hover:bg-stone-100 active:bg-stone-200 transition-all text-center group border border-stone-200"
                >
                  {/* Warning icon for urgent tasks */}
                  {kid.hasUrgent && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                  
                  {/* Eye icon for view switching - appears on tap/hover */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity">
                    <div className="w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                      <Eye className="w-2.5 h-2.5 text-white" />
                    </div>
                  </div>
                  
                  {/* Avatar */}
                  <div className="mb-3">
                    <AvatarOrInitial 
                      avatar={kid.avatar}
                      name={kid.name} 
                      size="md"
                      className="mx-auto"
                    />
                  </div>
                  
                  {/* Name */}
                  <h4 className="font-semibold text-slate-900 mb-2 truncate text-sm">{kid.name}</h4>
                  
                  {/* Progress */}
                  <p className="text-sm text-stone-600 mb-1">
                    {kid.completed}/{kid.total} done
                  </p>
                  
                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-stone-200 rounded-full mb-2">
                    <div 
                      className={`h-1.5 rounded-full transition-all ${
                        kid.percentage === 100 ? 'bg-emerald-500' :
                        kid.percentage >= 66 ? 'bg-teal-500' :
                        kid.percentage >= 33 ? 'bg-amber-500' : 'bg-stone-400'
                      }`}
                      style={{ width: `${kid.percentage}%` }}
                    />
                  </div>
                  
                  {/* Status */}
                  <p className="text-xs text-stone-500">
                    {kid.total === 0 ? 'No tasks set' :
                     kid.hasUrgent ? 'Has urgent task' :
                     kid.percentage === 100 ? 'All complete!' :
                     kid.percentage > 50 ? 'Good progress' : 
                     kid.completed === 0 ? 'Not started' : 'In progress'}
                  </p>
                </button>
              ))}
            </div>
            
            <p className="text-xs text-stone-500 mt-3 text-center">
              Tap any child to switch to their view
            </p>
          </div>
        </div>
      )}
      
      {/* Shopping List - Always visible */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm border-l-4 border-l-coral-500 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-coral-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Shopping List</h3>
              {actionableItems.pendingShopping > 0 && (
                <span className="bg-coral-100 text-coral-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {actionableItems.pendingShopping}
                </span>
              )}
            </div>
            <Link to="/shopping" className="text-coral-600 text-xs font-medium hover:text-coral-700 transition-colors">
              View All →
            </Link>
          </div>
          
          {/* Quick Add Input */}
          <form onSubmit={handleShoppingQuickAdd} className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500 w-4 h-4" />
              <input 
                ref={shoppingInputRef}
                type="text"
                value={shoppingQuickAdd}
                onChange={(e) => setShoppingQuickAdd(e.target.value)}
                placeholder="Add shopping item..."
                disabled={isAddingItem}
                className="w-full pl-10 pr-10 py-2.5 border border-stone-200 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500 transition-colors"
              />
              <button
                type="submit"
                disabled={!shoppingQuickAdd.trim() || isAddingItem}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-coral-600 hover:text-coral-700 disabled:text-stone-300 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </form>
          
          {/* Shopping Items */}
          <div className="space-y-2">
            {getRecentActiveShoppingItems(shoppingItems, 3).length > 0 ? (
              getRecentActiveShoppingItems(shoppingItems, 3).map((item, index) => {
                const requester = family?.members?.find(m => m.id === item.requested_by) || 
                                 family?.parents?.find(p => p.id === item.requested_by) ||
                                 family?.kids?.find(k => k.id === item.requested_by);
                
                return (
                  <div key={item.id || index} className="flex items-center gap-3 py-2">
                    <div className={`w-2 h-2 rounded-full ${
                      item.priority === 'urgent' ? 'bg-red-500' : 
                      item.priority === 'high' ? 'bg-amber-500' : 'bg-teal-500'
                    }`}></div>
                    <span className={`font-medium text-sm flex-1 truncate ${
                      item.status === 'purchased' ? 'text-stone-400 line-through' : 'text-slate-900'
                    }`}>
                      {item.name}
                      {item.quantity > 1 && <span className="text-xs text-stone-500 ml-1">×{item.quantity}</span>}
                    </span>
                    <span className="text-xs text-stone-500 font-medium">
                      {requester?.name || item.requested_by_name || 'Family'}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-4">
                <ShoppingCart className="w-6 h-6 text-stone-300 mx-auto mb-2" />
                <p className="text-xs text-stone-500">No items yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* What's New Section */}
      <div className="px-4 mb-4">
        <WhatsNewSection 
          isMobile={true}
          userRole={isKid ? 'kid' : 'parent'}
        />
      </div>
      
      {/* Reading Challenge Widget - Only for kids */}
      {isKid && (
        <div className="px-4 mb-4">
          <ReadingChallengeWidget
            activeChallenge={activeChallenge}
            currentBook={currentBook}
            readingStats={readingStats}
            isLoading={readingLoading}
            isMobile={true}
          />
        </div>
      )}
      
      {/* College Journey Widget - Only for kids */}
      {isKid && (
        <div className="px-4 mb-4">
          <CollegeJourneyWidget
            isMobile={true}
          />
        </div>
      )}
      
      {/* Next 3 Scheduled Items */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Next 3 Scheduled</h3>
            </div>
            <Link to="/calendar" className="text-teal-600 text-xs font-medium hover:text-teal-700 transition-colors">
              View All →
            </Link>
          </div>
          <div className="space-y-2">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map(event => (
                <UpcomingEventItem key={event.id} event={event} familyMembers={[...(family?.kids || []), ...(family?.parents || [])]} />
              ))
            ) : (
              <div className="text-center py-4">
                <Calendar className="w-6 h-6 text-stone-300 mx-auto mb-2" />
                <p className="text-xs text-stone-500 mb-1">No upcoming events</p>
                <p className="text-xs text-stone-400">Events within 7 days will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clean Priority Actions - Cute Badge Style */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4">
          <div className="space-y-1">
            {/* Messages */}
            {(actionableItems.urgentMessages > 0 || actionableItems.allMessages > 0) && (
              <CleanPriorityItem
                icon={MessageSquare}
                title={actionableItems.urgentMessages > 0 ? "Urgent Messages" : "New Messages"}
                count={actionableItems.urgentMessages || actionableItems.allMessages}
                area="messages"
                onClick={() => navigate('/messages')}
              />
            )}
            
            {/* RSVP Required */}
            {actionableItems.upcomingRSVPs > 0 && (
              <CleanPriorityItem
                icon={Calendar}
                title="RSVP Required"
                count={actionableItems.upcomingRSVPs}
                area="calendar"
                onClick={() => navigate('/calendar')}
              />
            )}
            
            {/* Vote on Activities */}
            {actionableItems.pendingVotes > 0 && (
              <CleanPriorityItem
                icon={Star}
                title="Vote on Activities"
                count={actionableItems.pendingVotes}
                area="family"
                onClick={() => navigate('/family')}
              />
            )}
            
            {/* Shopping */}
            {(actionableItems.urgentShopping > 0 || actionableItems.unsortedShopping > 0) && (
              <CleanPriorityItem
                icon={ShoppingCart}
                title={actionableItems.urgentShopping > 0 ? "Urgent Shopping" : "Sort Shopping"}
                count={actionableItems.urgentShopping || actionableItems.unsortedShopping}
                area="shopping"
                onClick={() => navigate('/shopping')}
              />
            )}
            
            {/* Complete Tasks */}
            {actionableItems.completableTodos > 0 && (
              <CleanPriorityItem
                icon={CheckSquare}
                title="Complete Tasks"
                count={actionableItems.completableTodos}
                area="tasks"
                onClick={() => navigate('/todos')}
              />
            )}
            
            {/* Allowance Jobs */}
            {actionableItems.pendingAllowance > 0 && (
              <CleanPriorityItem
                icon={DollarSign}
                title="Allowance Jobs"
                count={actionableItems.pendingAllowance}
                area="allowance"
                onClick={() => navigate('/allowance')}
              />
            )}
            
            {/* Planning */}
            {actionableItems.incompleteBig3 > 0 && (
              <CleanPriorityItem
                icon={Target}
                title="Plan Your Day"
                count={actionableItems.incompleteBig3}
                area="planning"
                onClick={() => navigate('/planning')}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Quick Add FAB */}
      <button
        onClick={() => navigate('/quick-add')}
        className="fixed bottom-20 right-4 w-14 h-14 bg-primary-500 text-white rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}